import {Tab, Tabs} from '@mui/material';
import {
  ConfigObjectType,
  DlMuiRiskScoreFrame,
  getLocalStorage,
  LightLayoutNodeFCProps,
  MinimizeLeftButton,
  TypeWithEtag,
  useDefaultScrollTo,
  useLocalStorage,
} from '@wallaby-ui/core';
import {MerieSchema, useFormatters, hospitalizationsContainer} from '@wallaby-ui/document';
import React, {useEffect} from 'react';

import {HospitalizationState} from './HospitalizationDetailLayoutV2';
import {HospitalizationDocumentsV2} from './HospitalizationDocumentsV2';

export const MINIMIZED_PATIENT_CARD_WIDTH = 52;
const PATIENT_CARD_PADDING_TOP = 82;

export type HospitalizationLeftPaneProps = {
  storageIdPrefix: string;
  data: TypeWithEtag<MerieSchema.components['schemas']['PatientCardDto']> | undefined;
  hospitalizationId: string;
  encounterId: number | undefined;
  documentId: string;
  reloadPatientCard: () => void;
  config: ConfigObjectType;
  state: HospitalizationState;
  changeState: (diff: Partial<HospitalizationState>) => void;
};

export const HospitalizationLeftPaneV2: React.FC<LightLayoutNodeFCProps & HospitalizationLeftPaneProps> = (props) => {
  const {
    storageIdPrefix,
    data,
    reloadPatientCard,
    config,
    hospitalizationId,
    encounterId,
    documentId,
    state,
    changeState,
    minimized,
    maximized,
    setMinimizedMaximized,
  } = props;

  const selectedTab = state.leftTabs.left ?? 0;
  const setSelectedTab = (newValue: number) => changeState({leftTabs: {...state.leftTabs, left: newValue}});
  const {t} = useFormatters();
  const [patientsDefaultScroll, setPatientsDefaultScroll] = useLocalStorage(`${storageIdPrefix}.patientsScroll`, 0);
  const patientsScrollRef = useDefaultScrollTo(patientsDefaultScroll);

  // Persist comment draft to localStorage so an in-progress edit survives navigating away and back (PDM-2050).
  const [comment, , setComment] = useLocalStorage<string | undefined>(`${storageIdPrefix}.comment`, data?.comment);
  const [commentFormatted, , setCommentFormatted] = useLocalStorage<string | undefined>(
    `${storageIdPrefix}.commentFormatted`,
    data?.commentFormatted ?? (data?.comment ? JSON.stringify(data.comment) : undefined)
  );

  useEffect(() => {
    // While the user is editing, keep their unsaved draft — do not overwrite with the server value on reload/remount.
    const isEditingComment = getLocalStorage(`${storageIdPrefix}.isEditingComment`, false);
    if (!isEditingComment && (data?.comment || data?.commentFormatted)) {
      setComment(data.comment);
      setCommentFormatted(data.commentFormatted ?? (data.comment ? JSON.stringify(data.comment) : undefined));
    }
  }, [data?.comment, data?.commentFormatted, storageIdPrefix, setComment, setCommentFormatted]);

  const handleReloadPatientCard = () => {
    reloadPatientCard();
  };

  if (!data) return <></>;

  const patientCardComponent = (
    <hospitalizationsContainer.PatientCardV2
      storageIdPrefix={storageIdPrefix}
      patientCardData={data}
      reloadPatientCard={handleReloadPatientCard}
      config={config}
      isCommentsMaximized={maximized}
      setIsCommentsMaximized={(newValue) => setMinimizedMaximized({maximized: newValue})}
      comment={comment}
      commentFormatted={commentFormatted}
      setComment={setComment}
      setCommentFormatted={setCommentFormatted}
    />
  );

  if (maximized) {
    return (
      <div style={{padding: 16}} data-page-box='true'>
        {patientCardComponent}
      </div>
    );
  }

  const isBigMode = !minimized || maximized;
  const showRiskScore = !minimized && !maximized;

  return (
    <div
      data-fill-available
      style={{
        width: minimized ? MINIMIZED_PATIENT_CARD_WIDTH : '100%',
        paddingTop: PATIENT_CARD_PADDING_TOP,
      }}
    >
      <div
        style={{
          width: '100%',
          height: 0,
          position: 'relative',
          top: -PATIENT_CARD_PADDING_TOP,
        }}
      >
        {showRiskScore && <DlMuiRiskScoreFrame data={data} left={`calc(50% - 53px)`} top={PATIENT_CARD_PADDING_TOP - 67} />}
      </div>
      <div data-fill-available={isBigMode} data-page-box='true' style={{display: 'flex', flexDirection: 'column'}}>
        <div
          data-rotate={isBigMode ? '' : 'left'}
          style={{
            display: 'flex',
            alignItems: 'stretch',
            padding: minimized ? 10 : '48px 10px 10px 10px',
          }}
        >
          <Tabs style={{flex: 1, paddingRight: 8}} className='dl-tab-row' value={selectedTab}>
            <Tab
              label={t('hospitalization.detailV2.patientTab')}
              onClick={() => {
                setSelectedTab(0);
                setMinimizedMaximized({minimized: false});
              }}
            />
            <Tab
              label={t('hospitalization.detailV2.documentsTab')}
              onClick={() => {
                setSelectedTab(1);
                setMinimizedMaximized({minimized: false});
              }}
            />
          </Tabs>
          <MinimizeLeftButton
            minimized={minimized}
            toggleMinimized={() => setMinimizedMaximized({minimized: !minimized, maximized: false})}
          />
        </div>
        {isBigMode && (
          <>
            {selectedTab === 0 && (
              <div
                data-fill-available
                style={{
                  flex: 1,
                  padding: '0px 10px 10px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
                ref={patientsScrollRef}
                onScroll={(event) => setPatientsDefaultScroll((event.target as HTMLElement).scrollTop)}
              >
                {patientCardComponent}
              </div>
            )}
            {selectedTab === 1 && (
              <div
                data-fill-available // NOTE: overflowY needs to be passed down by parent elements..
                style={{
                  flex: 1,
                  padding: '0 10px 10px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                }}
              >
                <HospitalizationDocumentsV2
                  patientCardId={data.id}
                  hospitalizationId={hospitalizationId}
                  encounterId={encounterId}
                  documentId={documentId}
                  config={config}
                  state={state}
                  changeState={changeState}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
